import UserInterface from "./UserInterface";
import {Vector as VectorSource} from 'ol/source';
import {Vector as VectorLayer} from 'ol/layer';
import {jsPDF} from "jspdf";
import Cutout from "./Cutout";
import Location, {LocationReactiveProps} from "./Location";
import {Translate} from "ol/interaction";
import LocationAddAction from "../ActionHistory/LocationAddAction";
import {Coordinate as olCoordinate} from "ol/coordinate";
import CoordinateConverter from "../Util/CoordinateConverter";
import predictName from "../Util/NamePredictor";
import {reactive} from "vue";
import {MapBrowserEvent} from "ol";
import * as FileSaver from "file-saver";

export default class LocationCollection {

    private reactiveLocationsProps: LocationReactiveProps[];

    readonly mapLayer: VectorLayer<any>;
    readonly mapSource: VectorSource<any>;
    readonly translateInteraction: Translate;

    private locations: Location[] = [];

    constructor(readonly userInterface: UserInterface) {
        this.reactiveLocationsProps = reactive([]);

        const olMap = userInterface.getMap().getOpenlayersMap();

        this.mapSource = new VectorSource();

        this.mapLayer = new VectorLayer({
            source: this.mapSource,
        });

        this.translateInteraction = new Translate({
            layers: [this.mapLayer],
            condition: (mapBrowserEvent: MapBrowserEvent<any>): boolean => {
                // You may not drag in locked mode
                if (this.userInterface.isLocked()) {
                    return false;
                }

                if (this.userInterface.getRouteCollection().getFocusedRoute()?.getEditMode() === 'intermediate') {
                    // Work-around for glitch where marker is snapped to intermediate points when moving a marker
                    // that was originally located near an intermediate point
                    return false;
                }

                return true;
            },
        });

        olMap.addLayer(this.mapLayer);
        olMap.addInteraction(this.translateInteraction);

        this.translateInteraction.on('translatestart', (e) => {
            document.body.style.cursor = 'grabbing';
        });

        this.translateInteraction.on('translateend', (e) => {
            document.body.style.cursor = 'default';

            const feature = e.features.getArray()[0];

            if (!feature) {
                return;
            }

            const location: Location = feature.get('location');

            if (!location) {
                return;
            }

            if (location.isMouseOver()) {
                document.body.style.cursor = 'grab';
            }

            location.updateAfterMove();
        });

        olMap.on('singleclick', (e) => {
            if (e.defaultPrevented) {
                return;
            }

            const locationFeature = olMap.forEachFeatureAtPixel(e.pixel, function (feature, layer) {
                if (feature && feature.get('location')) {
                    return feature;
                }
            }, {
                layerFilter: (layer) => {
                    return layer == this.mapLayer;
                }
            });

            if (locationFeature) {
                e.preventDefault();
                e.stopPropagation();

                const location = locationFeature.get('location');
                this.userInterface.toggleCoordinatePanelForLocation(location);
            }
        });
    }

    public getOpenlayersMap()
    {
        return this.userInterface.getMap().getOpenlayersMap();
    }

    public getLocations()
    {
        return this.locations;
    }

    public find(id: number)
    {
        return this.locations.find(location => location.id === id);
    }

    public getReactiveLocationsProps(): LocationReactiveProps[]
    {
        return this.reactiveLocationsProps;
    }

    public updateReactiveLocationsProps(): void
    {
        this.reactiveLocationsProps.splice(0, this.reactiveLocationsProps.length, ...this.locations.map(location => location.reactiveProps));
    }

    public addLocation(coordinate: olCoordinate|null = null, name: string|null = null)
    {
        const location = new Location(this, coordinate);
        location.setName(
            name
            || predictName(this.locations.map((location) => location.getName()))
            || 'Punt ' + (location.id + 1)
        );

        this.userInterface.actionHistory.addAction(new LocationAddAction(
            location,
            this.locations.length
        ));

        return location;
    }

    public attachLocation(location: Location, position: number)
    {
        location.mouseout(false);
        location.addToMap();
        this.locations.splice(position, 0, location);
        this.updateReactiveLocationsProps();
        this.userInterface.showObjectListPanel('location');
    }

    public detachLocation(location: Location)
    {
        const index = this.locations.indexOf(location);
        if(index === -1) {
            throw new Error('Invalid location');
        }

        location.removeFromMap();
        this.locations.splice(index, 1);
        this.updateReactiveLocationsProps();
        this.userInterface.showObjectListPanel('location');
    }

    public moveLocationInList(location: Location, newIndex: number): void {
        const index = this.locations.indexOf(location);
        if (index === -1) {
            throw new Error('Invalid location');
        }

        this.locations.splice(index, 1);
        this.locations.splice(newIndex, 0, location);

        this.updateReactiveLocationsProps();
        this.userInterface.showObjectListPanel('location');
    }

    public duplicateLocation(sourceLocation: Location)
    {
        const coordinate = sourceLocation.getCoordinate();
        const location = this.addLocation();
        location.setCoordinate(coordinate);
        location.setColorAndMarkerType(sourceLocation.getColor(), sourceLocation.getMarkerType());

        location.setDrawMarker(sourceLocation.getDrawMarker());
        location.setDrawName(sourceLocation.getDrawName());
    }

    public drawOnPdf(doc: jsPDF, cutout: Cutout<any, any, any>)
    {
        for (const location of this.locations) {
            if (location.isVisible()) {
                location.drawMarkerOnPdf(doc, cutout);
            }
        }

        for (const location of this.locations) {
            if (location.isVisible()) {
                location.drawNameOnPdf(doc, cutout);
            }
        }
    }

    public downloadAllXlsx()
    {
        const coordinateSystemCodes = [
            'EPSG:28992',
            'EPSG:25832',
            'EPSG:4326',
        ];

        const formatColumns = [];

        const dummyCoord = CoordinateConverter.getCoordinateSystem('EPSG:4326').make(0, 0);

        for (const code of coordinateSystemCodes) {
            const coordinateSystem = CoordinateConverter.getCoordinateSystem(code);
            const systemCoord = CoordinateConverter.convert(dummyCoord, coordinateSystem);
            const formats = systemCoord.formats();

            for (const format in formats) {
                formatColumns.push({
                    name: coordinateSystem.name + ' (' + format + ')',
                    formatter: (location: Location) => {
                        try {
                            return location.getFormattedCoordinate(coordinateSystem, format);
                        } catch (e) {
                            return '-';
                        }
                    }
                });
            }
        }

        let has_descriptions = false;
        for (const location of this.locations) {
            if (location.reactiveProps.description_text != '') {
                has_descriptions = true;
                break;
            }
        }

        const columns = [
            {name: 'Punt'}
        ];

        if (has_descriptions) {
            columns.push({name: 'Beschrijving'});
        }

        for (const column of formatColumns) {
            columns.push({name: column.name});
        }

        const rows = [];
        const hyperlinkRows = [];
        for (const location of this.locations) {
            const row = [
                location.getName(),
            ];
            if (has_descriptions) {
                const [richTextEntries, hyperlinks] = location.getExcelRichTextDescription(hyperlinkRows.length);
                row.push({richText: richTextEntries});

                for (const hyperlink of hyperlinks) {
                    hyperlinkRows.push([
                        '[' + hyperlink.linkId + ']',
                        location.getName(),
                        {richText: hyperlink.richText},
                        {text: hyperlink.href, hyperlink: hyperlink.href},
                    ]);
                }
            }
            for (const column of formatColumns) {
                row.push(column.formatter(location));
            }

            rows.push(row);
        }

        if (rows.length === 0) {
            // Prevent excel crash
            rows.push(columns.map(() => ''));
        }

        import('exceljs').then(async ({default: ExcelJS}) => {
            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'plattekaart.nl';
            workbook.created = new Date();

            const sheet = workbook.addWorksheet('Punten');
            sheet.addTable({
                name: 'Punten',
                ref: 'A1',
                headerRow: true,
                style: {
                    showRowStripes: true,
                },
                columns: columns,
                rows: rows,
            });

            for (let i = 0; i < columns.length; i++) {
                sheet.columns[i].width = 20;
            }

            if (hyperlinkRows.length > 0) {
                const linkSheet = workbook.addWorksheet('Links');
                linkSheet.addTable({
                    name: 'Links',
                    ref: 'A1',
                    headerRow: true,
                    style: {
                        showRowStripes: true,
                    },
                    columns: [
                        {name: '#'},
                        {name: 'Punt'},
                        {name: 'Link tekst'},
                        {name: 'URL'},
                    ],
                    rows: hyperlinkRows,
                });

                linkSheet.columns[0].width = 5;
                linkSheet.columns[1].width = 20;
                linkSheet.columns[2].width = 20;
                linkSheet.columns[3].width = 80;
            }

            const buffer = await workbook.xlsx.writeBuffer();
            FileSaver.saveAs(
                new Blob([buffer], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"}),
                "punten.xlsx"
            );
        });
    }
}
